import React, { useState, useEffect } from 'react';
import {
    Card,
    Row,
    Col,
    Avatar,
    Typography,
    Button,
    Input,
    Table,
    Spin,
    Empty,
    Badge,
    Space,
    Divider,
    Tag,
    Layout,
    PageHeader,
    Alert,
    Tooltip,
    Image,
    Modal,
    Descriptions
} from 'antd';
import {
    UserOutlined,
    SearchOutlined,
    ArrowLeftOutlined,
    CalendarOutlined,
    FileTextOutlined,
    MedicineBoxOutlined,
    RightOutlined,
    FilterOutlined,
    StarFilled,
    ClockCircleOutlined,
    CheckCircleOutlined,
    PictureOutlined,
    EyeOutlined,
    InfoCircleOutlined,
    CloseOutlined
} from '@ant-design/icons';
import { getImg } from '../../../Api/getImg';
import drEasyApi from '../../../Api/Customapi';
import { useLanguage } from '../../../context/LanguageContext';

const { Title, Text, Paragraph } = Typography;
const { Search } = Input;
const { Content } = Layout;

// Custom styles using your brand colors
const customStyles = {
    primaryColor: '#298E9F',
    textPrimaryColor: '#195761',
    cardColor: '#EBF8FA',
    placeholderColor: '#759AA0',
    primaryHover: '#1F6F7D',
    secondaryColor: '#FEBA17',
    secondaryHover: '#E5A515',
    pageColor: '#F7FCFD',
};

const MyReports = () => {
    const [doctors, setDoctors] = useState([]);
    const [selectedDoctor, setSelectedDoctor] = useState(null);
    const [reports, setReports] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadingReports, setLoadingReports] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedReport, setSelectedReport] = useState(null);
    const [modalVisible, setModalVisible] = useState(false);
    const { t } = useLanguage();

    useEffect(() => {
        fetchDoctors();
    }, []);

    const fetchDoctors = async () => {
        try {
            setLoading(true);
            const response = await drEasyApi.get(`api/Doctor/myDoctors`)
            setDoctors(response.data);
        } catch (error) {
            console.error('Error fetching doctors:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchDoctorReports = async (doctorId) => {
        try {
            setLoadingReports(true);
            const response = await drEasyApi.get(`api/Reports/GetMyReport?DoctorId=${doctorId}`)

       
            setReports(response.data);
        } catch (error) {
            console.error('Error fetching reports:', error);
        } finally {
            setLoadingReports(false);
        }
    };

    const handleDoctorSelect = (doctor) => {
        setSelectedDoctor(doctor);
        fetchDoctorReports(doctor.doctorId);
    };

    const handleBack = () => {
        setSelectedDoctor(null);
        setReports(null);
    };

    const handleReportClick = (report) => {
        setSelectedReport(report);
        setModalVisible(true);
    };

    const handleModalClose = () => {
        setModalVisible(false);
        setSelectedReport(null);
    };

    

    const filteredDoctors = doctors.filter(doctor =>
        doctor.doctorName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        doctor.specialization.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const formatDateLong = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const renderCellContent = (cell, isModal = false) => {
        if (!cell) return '-';

        const hasText = cell.text && cell.text.trim();
        const hasPhoto = cell.photoId;

        return (
            <div>
                {hasText && (
                    <Text style={{
                        color: customStyles.textPrimaryColor,
                        fontSize: isModal ? '16px' : '14px',
                        display: 'block',
                        marginBottom: hasPhoto ? '12px' : '0',
                        whiteSpace: 'pre-line',
                        lineHeight: '1.6'
                    }}>
                        {cell.text.trim()}
                    </Text>
                )}
                {hasPhoto && (
                    <div style={{ marginTop: hasText ? '12px' : '0' }}>
                        <Image
                            src={getImg(cell.photoId)}
                            alt="Medical Report Image"
                            style={{
                                maxWidth: isModal ? '100%' : '200px',
                                maxHeight: isModal ? '300px' : '120px',
                                borderRadius: '12px',
                                border: `2px solid ${customStyles.cardColor}`,
                                objectFit: 'cover'
                            }}
                            preview={{
                                mask: (
                                    <div style={{
                                        background: 'rgba(0,0,0,0.6)',
                                        color: 'white',
                                        padding: '8px 12px',
                                        borderRadius: '6px',
                                        fontSize: '14px'
                                    }}>
                                        <PictureOutlined /> {t('myReports.reportModal.viewImage')}
                                    </div>
                                )
                            }}
                            fallback="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjQiIGhlaWdodD0iNjQiIHZpZXdCb3g9IjAgMCA2NCA2NCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjY0IiBoZWlnaHQ9IjY0IiBmaWxsPSIjRjVGNUY1Ii8+CjxwYXRoIGQ9Ik0yMCAyMEg0NFY0NEgyMFYyMFoiIGZpbGw9IiNEOUQ5RDkiLz4KPHBhdGggZD0iTTI2IDI4TDMwIDMyTDM4IDI0TDQ0IDMwVjQwSDIwVjMwTDI2IDI4WiIgZmlsbD0iI0JGQkZCRiIvPgo8L3N2Zz4K"
                        />
                    </div>
                )}
                {!hasText && !hasPhoto && (
                    <Text style={{
                        color: customStyles.placeholderColor,
                        fontSize: isModal ? '16px' : '14px',
                        fontStyle: 'italic'
                    }}>
                        {t('myReports.reportCard.noData')}
                    </Text>
                )}
            </div>
        );
    };

    const getReportPreview = (report) => {
        if (!reports || !reports.columns) return t('myReports.reportCard.noData');

        // Get first non-empty cell for preview
        const firstCell = report.cells.find(cell =>
            cell.text && cell.text.trim() || cell.photoId
        );

        if (!firstCell) return t('myReports.reportCard.noData');

        if (firstCell.text) {
            return firstCell.text.length > 100
                ? firstCell.text.substring(0, 100) + '...'
                : firstCell.text;
        }

        if (firstCell.photoId) {
            return t('myReports.reportCard.previewImage');
        }

        return t('myReports.reportCard.previewDefault');
    };

    const getReportDataCount = (report) => {
        if (!report.cells) return 0;
        return report.cells.filter(cell =>
            (cell.text && cell.text.trim()) || cell.photoId
        ).length;
    };

    const renderReportCards = () => {
        if (!reports || !reports.rows) return null;

        return (
            <Row gutter={[24, 24]}>
                {reports.rows.map((report, index) => {
                    const dataCount = getReportDataCount(report);
                    const preview = getReportPreview(report);

                    return (
                        <Col xs={24} sm={12} lg={8} key={report.bookingId || index}>
                            <Card
                                hoverable
                                onClick={() => handleReportClick(report)}
                                style={{
                                    height: '100%',
                                    borderRadius: '20px',
                                    border: 'none',
                                    background: 'rgba(255, 255, 255, 0.95)',
                                    backdropFilter: 'blur(10px)',
                                    boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                    transform: 'translateY(0px)',
                                    position: 'relative',
                                    overflow: 'hidden'
                                }}
                                bodyStyle={{ padding: '24px' }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = 'translateY(-4px)';
                                    e.currentTarget.style.boxShadow = '0 16px 48px rgba(0,0,0,0.15)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = 'translateY(0px)';
                                    e.currentTarget.style.boxShadow = '0 8px 32px rgba(0,0,0,0.1)';
                                }}
                            >
                                {/* Header */}
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'flex-start',
                                    marginBottom: '16px'
                                }}>
                                    <div style={{ flex: 1 }}>
                                        <Space align="start">
                                            <div style={{
                                                background: `linear-gradient(135deg, ${customStyles.primaryColor} 0%, ${customStyles.secondaryColor} 100%)`,
                                                borderRadius: '12px',
                                                padding: '8px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}>
                                                <FileTextOutlined style={{ color: 'white', fontSize: '20px' }} />
                                            </div>
                                            <div>
                                                <Title
                                                    level={5}
                                                    style={{
                                                        margin: 0,
                                                        color: customStyles.textPrimaryColor,
                                                        fontWeight: '600',
                                                        fontSize: '16px'
                                                    }}
                                                >
                                                    {t('myReports.reportCard.title')}
                                                </Title>
                                               
                                            </div>
                                        </Space>
                                    </div>
                                </div>

                                {/* Date */}
                                <div style={{ marginBottom: '16px' }}>
                                    <Tag
                                        color={customStyles.primaryColor}
                                        icon={<CalendarOutlined />}
                                        style={{
                                            borderRadius: '20px',
                                            padding: '6px 12px',
                                            fontWeight: '500',
                                            fontSize: '13px'
                                        }}
                                    >
                                        {formatDate(report.bookingDate)}
                                    </Tag>
                                </div>

                                {/* Preview Content */}
                                <div style={{
                                    marginBottom: '20px',
                                    minHeight: '80px',
                                    background: `${customStyles.cardColor}30`,
                                    borderRadius: '12px',
                                    padding: '16px',
                                    border: `1px solid ${customStyles.cardColor}`
                                }}>
                                    <Text style={{
                                        color: customStyles.textPrimaryColor,
                                        fontSize: '14px',
                                        lineHeight: '1.5',
                                        display: '-webkit-box',
                                        WebkitLineClamp: 4,
                                        WebkitBoxOrient: 'vertical',
                                        overflow: 'hidden'
                                    }}>
                                        {preview}
                                    </Text>
                                </div>

                                <Divider style={{ margin: '16px 0', borderColor: customStyles.cardColor }} />

                                {/* Footer */}
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center'
                                }}>
                                    <Button
                                        type="link"
                                        icon={<EyeOutlined className='text-primary' />}
                                        style={{
                                            color: customStyles.primaryColor,
                                            fontWeight: '600',
                                            padding: 0,
                                            height: 'auto'
                                        }}
                                    >
                                        {t('myReports.reportCard.viewDetails')}
                                    </Button>
                                </div>
                            </Card>
                        </Col>
                    );
                })}
            </Row>
        );
    };

    const renderReportModal = () => {
        if (!selectedReport || !reports || !reports.columns) return null;

        const reportData = [];
        reports.columns.forEach(column => {
            const cell = selectedReport.cells.find(c => c.columnId === column.id);
            if (cell && ((cell.text && cell.text.trim()) || cell.photoId)) {
                reportData.push({
                    label: column.title || 'Notes',
                    content: renderCellContent(cell, true)
                });
            }
        });

        return (
            <Modal
                title={
                    <div style={{
                        background: `linear-gradient(135deg, ${customStyles.primaryColor} 0%, ${customStyles.primaryHover} 100%)`,
                        margin: '-24px -24px 0 -24px',
                        padding: '24px',
                        color: 'white',
                        borderRadius: '8px 8px 0 0'
                    }}>
                        <Space>
                            <FileTextOutlined style={{ fontSize: '24px' }} />
                            <div>
                                <Title level={3} style={{ color: 'white', margin: 0, fontSize: '20px' }}>
                                    {t('myReports.reportModal.title')}
                                </Title>
                                <Text style={{ color: 'rgba(255,255,255,0.9)', fontSize: '14px' }}>
                                    {formatDateLong(selectedReport.bookingDate)}
                                </Text>
                            </div>
                        </Space>
                    </div>
                }
                open={modalVisible}
                onCancel={handleModalClose}
                footer={
                    <div style={{ textAlign: 'right' }}>
                        <Button
                            onClick={handleModalClose}
                            style={{
                                background: customStyles.primaryColor,
                                borderColor: customStyles.primaryColor,
                                color: 'white',
                                borderRadius: '8px',
                                fontWeight: '500'
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.background = customStyles.primaryHover;
                                e.target.style.borderColor = customStyles.primaryHover;
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.background = customStyles.primaryColor;
                                e.target.style.borderColor = customStyles.primaryColor;
                            }}
                        >
                            {t('myReports.reportModal.close')}
                        </Button>
                    </div>
                }
                width={800}
                style={{
                    '.ant-modal-content': {
                        borderRadius: '16px',
                        overflow: 'hidden'
                    }
                }}
                closeIcon={
                    <CloseOutlined style={{
                        color: 'white',
                        fontSize: '16px',
                        background: 'rgba(255,255,255,0.2)',
                        borderRadius: '50%',
                        padding: '8px'
                    }} />
                }
            >
                <div style={{ padding: '24px 0' }}>
                    {reportData.length > 0 ? (
                        <div>
                            {reportData.map((item, index) => (
                                <div key={index} style={{
                                    marginBottom: '32px',
                                    padding: '24px',
                                    background: index % 2 === 0 ? `${customStyles.cardColor}20` : 'transparent',
                                    borderRadius: '12px',
                                    border: index % 2 === 0 ? `1px solid ${customStyles.cardColor}` : 'none'
                                }}>
                                    <Title
                                        level={5}
                                        style={{
                                            color: customStyles.textPrimaryColor,
                                            marginBottom: '12px',
                                            fontWeight: '600',
                                            fontSize: '16px'
                                        }}
                                    >
                                        {item.label}
                                    </Title>
                                    <div>
                                        {item.content}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description={
                                <span style={{ color: customStyles.placeholderColor, fontSize: '16px' }}>
                                    {t('myReports.reportModal.noData')}
                                </span>
                            }
                        />
                    )}
                </div>
            </Modal>
        );
    };

    if (loading) {
        return (
            <Layout style={{
                minHeight: '100vh',
                background: `linear-gradient(135deg, ${customStyles.pageColor} 0%, ${customStyles.cardColor} 100%)`
            }}>
                <Content style={{
                    padding: '48px 24px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                }}>
                    <Card
                        style={{
                            textAlign: 'center',
                            minWidth: '400px',
                            background: 'rgba(255, 255, 255, 0.95)',
                            backdropFilter: 'blur(10px)',
                            borderRadius: '24px',
                            border: 'none',
                            boxShadow: '0 20px 40px rgba(0,0,0,0.1)'
                        }}
                        bodyStyle={{ padding: '48px 32px' }}
                    >
                        <Spin size="large" style={{ color: customStyles.primaryColor }} />
                        <Title
                            level={3}
                            style={{
                                marginTop: '24px',
                                color: customStyles.textPrimaryColor,
                                fontWeight: '600'
                            }}
                        >
                            {t('myReports.loading.doctors')}
                        </Title>
                        <Text style={{ color: customStyles.placeholderColor, fontSize: '16px' }}>
                            {t('myReports.loading.doctorsSubtitle')}
                        </Text>
                    </Card>
                </Content>
            </Layout>
        );
    }

    return (
        <Layout style={{
            minHeight: '100vh',
            background: `linear-gradient(135deg, ${customStyles.pageColor} 0%, ${customStyles.cardColor} 100%)`
        }}>
            <Content style={{ padding: '32px 24px' }}>
                {!selectedDoctor ? (
                    <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
                        {/* Enhanced Header */}
                        <div style={{
                            background: 'rgba(255, 255, 255, 0.95)',
                            backdropFilter: 'blur(10px)',
                            borderRadius: '24px',
                            padding: '32px',
                            marginBottom: '32px',
                            border: 'none',
                            boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
                            background: `linear-gradient(135deg, ${customStyles.primaryColor} 0%, ${customStyles.primaryHover} 100%)`,
                            color: 'white'
                        }}>
                            <Title
                                level={1}
                                style={{
                                    color: 'white',
                                    margin: '0 0 8px 0',
                                    fontSize: '36px',
                                    fontWeight: '700'
                                }}
                            >
                                {t('myReports.title')}
                            </Title>
                            <Text style={{
                                color: 'rgba(255, 255, 255, 0.9)',
                                fontSize: '18px',
                                fontWeight: '400'
                            }}>
                                {t('myReports.subtitle')}
                            </Text>
                        </div>

                        {/* Enhanced Search Bar */}
                        <Card
                            style={{
                                marginBottom: '32px',
                                borderRadius: '20px',
                                border: 'none',
                                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                                background: 'rgba(255, 255, 255, 0.95)',
                                backdropFilter: 'blur(10px)'
                            }}
                            bodyStyle={{ padding: '24px' }}
                        >
                            <Row gutter={16} align="middle">
                                <Col flex="auto">
                                    <Search
                                        placeholder={t('myReports.search.placeholder')}
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        size="large"
                                        prefix={<SearchOutlined style={{ color: customStyles.primaryColor }} />}
                                        style={{
                                            '.ant-input': {
                                                borderRadius: '12px',
                                                border: `2px solid ${customStyles.cardColor}`,
                                                fontSize: '16px',
                                                padding: '12px 16px'
                                            }
                                        }}
                                    />
                                </Col>
                            </Row>
                        </Card>

                        {/* Enhanced Doctors Grid */}
                        <Row gutter={[24, 24]}>
                            {filteredDoctors.map((doctor) => (
                                <Col xs={24} sm={12} lg={8} xl={6} key={doctor.doctorId}>
                                    <Card
                                        hoverable
                                        onClick={() => handleDoctorSelect(doctor)}
                                        style={{
                                            height: '100%',
                                            borderRadius: '20px',
                                            border: 'none',
                                            background: 'rgba(255, 255, 255, 0.95)',
                                            backdropFilter: 'blur(10px)',
                                            boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                                            cursor: 'pointer',
                                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                            transform: 'translateY(0px)'
                                        }}
                                        bodyStyle={{ padding: '32px 24px' }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.transform = 'translateY(-8px)';
                                            e.currentTarget.style.boxShadow = '0 16px 48px rgba(0,0,0,0.15)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.transform = 'translateY(0px)';
                                            e.currentTarget.style.boxShadow = '0 8px 32px rgba(0,0,0,0.1)';
                                        }}
                                    >
                                        <div style={{ textAlign: 'center', marginBottom: '20px', position: 'relative' }}>
                                            <Badge
                                                dot={doctor.hasReport}
                                                status={doctor.hasReport ? 'success' : 'default'}
                                                offset={[-12, 12]}
                                                style={{
                                                    '.ant-badge-dot': {
                                                        width: '12px',
                                                        height: '12px'
                                                    }
                                                }}
                                            >
                                                <div style={{
                                                    background: `linear-gradient(135deg, ${customStyles.primaryColor} 0%, ${customStyles.secondaryColor} 100%)`,
                                                    borderRadius: '50%',
                                                    padding: '4px',
                                                    display: 'inline-block'
                                                }}>
                                                    <Avatar
                                                        size={88}
                                                        src={getImg(doctor.photoId)}
                                                        icon={!doctor.photoId ? <UserOutlined /> : null}
                                                        style={{
                                                            background: 'white',
                                                            color: customStyles.primaryColor,
                                                            border: '4px solid white'
                                                        }}
                                                    />
                                                </div>
                                            </Badge>
                                            {doctor.hasReport && (
                                                <div style={{
                                                    position: 'absolute',
                                                    top: '0',
                                                    right: 'calc(50% - 60px)',
                                                    background: customStyles.secondaryColor,
                                                    borderRadius: '12px',
                                                    padding: '2px 8px',
                                                    fontSize: '10px',
                                                    color: 'white',
                                                    fontWeight: '600'
                                                }}>
                                                    {t('myReports.doctorCard.active')}
                                                </div>
                                            )}
                                        </div>

                                        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                                            <Title
                                                level={4}
                                                style={{
                                                    margin: '0 0 8px 0',
                                                    color: customStyles.textPrimaryColor,
                                                    fontWeight: '600',
                                                    fontSize: '18px'
                                                }}
                                            >
                                                {t("common.dr")} {doctor.doctorName}
                                            </Title>
                                            <Tag
                                                color={customStyles.cardColor}
                                                style={{
                                                    color: customStyles.textPrimaryColor,
                                                    borderRadius: '16px',
                                                    padding: '4px 12px',
                                                    border: 'none',
                                                    fontWeight: '500'
                                                }}
                                            >
                                                {doctor.specialization}
                                            </Tag>
                                        </div>

                                        <Divider style={{ margin: '20px 0', borderColor: customStyles.cardColor }} />

                                        <div style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center'
                                        }}>
                                            <Space>
                                                {doctor.hasReport ? (
                                                    <CheckCircleOutlined style={{ color: customStyles.secondaryColor, fontSize: '16px' }} />
                                                ) : (
                                                    <ClockCircleOutlined style={{ color: customStyles.placeholderColor, fontSize: '16px' }} />
                                                )}
                                                <Text
                                                    style={{
                                                        fontSize: '13px',
                                                        color: doctor.hasReport ? customStyles.textPrimaryColor : customStyles.placeholderColor,
                                                        fontWeight: '500'
                                                    }}
                                                >
                                                    {doctor.hasReport ? t('myReports.doctorCard.reportsAvailable') : t('myReports.doctorCard.noReports')}
                                                </Text>
                                            </Space>
                                            <div style={{
                                                background: customStyles.primaryColor,
                                                borderRadius: '50%',
                                                width: '32px',
                                                height: '32px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}>
                                                <RightOutlined style={{ color: 'white', fontSize: '14px' }} />
                                            </div>
                                        </div>
                                    </Card>
                                </Col>
                            ))}
                        </Row>

                        {filteredDoctors.length === 0 && (
                            <Card style={{
                                borderRadius: '20px',
                                border: 'none',
                                boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                                background: 'rgba(255, 255, 255, 0.95)',
                                backdropFilter: 'blur(10px)'
                            }}>
                                <Empty
                                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                                    description={
                                        <span style={{ color: customStyles.placeholderColor, fontSize: '16px' }}>
                                            {t('myReports.empty.noMatch')}
                                        </span>
                                    }
                                />
                            </Card>
                        )}
                    </div>
                ) : (
                    // Enhanced Reports View with Cards
                    <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
                        {/* Enhanced Header */}
                        <div style={{
                            background: `linear-gradient(135deg, ${customStyles.primaryColor} 0%, ${customStyles.primaryHover} 100%)`,
                            borderRadius: '24px',
                            padding: '32px',
                            marginBottom: '32px',
                            color: 'white',
                            boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
                        }}>
                            <Space align="start" style={{ width: '100%', justifyContent: 'space-between' }}>
                                <div>
                                    <Button
                                        onClick={handleBack}
                                        icon={<ArrowLeftOutlined />}
                                        style={{
                                            background: 'rgba(255, 255, 255, 0.2)',
                                            border: 'none',
                                            color: 'white',
                                            borderRadius: '12px',
                                            marginBottom: '16px'
                                        }}
                                    >
                                        {t('myReports.doctorsView.backButton')}
                                    </Button>
                                    <Title
                                        level={1}
                                        style={{
                                            color: 'white',
                                            margin: '0 0 8px 0',
                                            fontSize: '32px',
                                            fontWeight: '700'
                                        }}
                                    >
                                        {t("common.dr")} {selectedDoctor.doctorName}
                                    </Title>
                                    <Text style={{
                                        color: 'rgba(255, 255, 255, 0.9)',
                                        fontSize: '18px'
                                    }}>
                                        {selectedDoctor.specialization} {t('myReports.doctorsView.specialist')}
                                    </Text>
                                </div>
                                <div style={{
                                    background: 'rgba(255, 255, 255, 0.2)',
                                    borderRadius: '16px',
                                    padding: '16px',
                                    textAlign: 'center'
                                }}>
                                    <MedicineBoxOutlined style={{ fontSize: '32px', marginBottom: '8px' }} />
                                    <div style={{ fontSize: '14px', opacity: 0.9 }}>{t('myReports.doctorsView.medicalReports')}</div>
                                </div>
                            </Space>
                        </div>

                        <Card
                            style={{
                                borderRadius: '20px',
                                border: 'none',
                                boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                                background: 'rgba(255, 255, 255, 0.95)',
                                backdropFilter: 'blur(10px)'
                            }}
                            bodyStyle={{ padding: '32px' }}
                        >
                            {loadingReports ? (
                                <div style={{ textAlign: 'center', padding: '60px 0' }}>
                                    <Spin size="large" style={{ color: customStyles.primaryColor }} />
                                    <Title
                                        level={3}
                                        style={{
                                            marginTop: '24px',
                                            color: customStyles.textPrimaryColor,
                                            fontWeight: '600'
                                        }}
                                    >
                                        {t('myReports.loading.reports')}
                                    </Title>
                                    <Text style={{ color: customStyles.placeholderColor, fontSize: '16px' }}>
                                        {t('myReports.loading.reportsSubtitle')}
                                    </Text>
                                </div>
                            ) : reports && reports.rows && reports.rows.length > 0 ? (
                                <div>
                                    <div style={{
                                        marginBottom: '32px',
                                        padding: '24px',
                                        background: customStyles.cardColor,
                                        borderRadius: '16px',
                                        border: `2px solid ${customStyles.primaryColor}20`
                                    }}>
                                        <Space>
                                            <FileTextOutlined style={{
                                                color: customStyles.primaryColor,
                                                fontSize: '24px'
                                            }} />
                                            <div>
                                                <Title
                                                    level={4}
                                                    style={{
                                                        margin: 0,
                                                        color: customStyles.textPrimaryColor,
                                                        fontWeight: '600'
                                                    }}
                                                >
                                                    {t('myReports.doctorsView.historyTitle')}
                                                </Title>
                                                <Text style={{ color: customStyles.placeholderColor }}>
                                                    {reports.rows.length} {reports.rows.length === 1 
                                                        ? t('myReports.doctorsView.historySubtitle')
                                                        : t('myReports.doctorsView.historySubtitlePlural')}
                                                </Text>
                                            </div>
                                        </Space>
                                    </div>

                                    {renderReportCards()}
                                </div>
                            ) : (
                                <div style={{ textAlign: 'center', padding: '60px 0' }}>
                                    <div style={{
                                        background: customStyles.cardColor,
                                        borderRadius: '50%',
                                        width: '120px',
                                        height: '120px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        margin: '0 auto 24px'
                                    }}>
                                        <FileTextOutlined style={{
                                            fontSize: '48px',
                                            color: customStyles.primaryColor
                                        }} />
                                    </div>
                                    <Title
                                        level={3}
                                        style={{
                                            color: customStyles.textPrimaryColor,
                                            marginBottom: '8px'
                                        }}
                                    >
                                        {t('myReports.doctorsView.noReportsTitle')}
                                    </Title>
                                    <Text style={{
                                        color: customStyles.placeholderColor,
                                        fontSize: '16px'
                                    }}>
                                       {t("common.dr")} {selectedDoctor.doctorName} {t('myReports.doctorsView.noReportsSubtitle')}
                                    </Text>
                                </div>
                            )}
                        </Card>
                    </div>
                )}

                {/* Report Details Modal */}
                {renderReportModal()}
            </Content>
        </Layout>
    );
};

export default MyReports;